/**
 * Player identity sets: COLOURS, TONES, MOVES, ICONS (see §4 and §10).
 *
 * Identity is communicated WITHOUT reading: a full-screen colour + a large
 * animal icon + a distinct tone. The curated sets guarantee it always looks
 * and sounds good no matter the random sequence.
 */

import type { AssistLevel } from './tuning.js';

/**
 * Colour-blind-safe, high-contrast palette. Order matters: players are assigned
 * in this order so early joiners get the most distinct hues. `name` is spoken
 * aloud (Web Speech) as an optional assist; it is never required reading.
 */
export const COLOURS = [
  { id: 'blue', name: 'blue', hex: '#1f78ff', text: '#ffffff' },
  { id: 'amber', name: 'orange', hex: '#ff9500', text: '#1a1200' },
  { id: 'green', name: 'green', hex: '#1db954', text: '#04210f' },
  { id: 'magenta', name: 'pink', hex: '#ff4fd8', text: '#2a0322' },
  { id: 'violet', name: 'purple', hex: '#8a5bff', text: '#ffffff' },
] as const;

export type ColourId = (typeof COLOURS)[number]['id'];

/**
 * Tones are notes of a C major pentatonic scale (C D E G A). ANY random
 * sequence built from these is automatically consonant — no dissonance, ever.
 * Frequencies are equal-tempered, octave 4–5.
 */
export const TONES = [
  { id: 'C', freq: 523.25 }, // C5
  { id: 'D', freq: 587.33 }, // D5
  { id: 'E', freq: 659.25 }, // E5
  { id: 'G', freq: 783.99 }, // G5
  { id: 'A', freq: 880.0 }, //  A5
] as const;

/**
 * Camera-detectable signature moves for Move Simon (§10). Each has a simple
 * icon so no reading is needed. `verb` is spoken as an optional assist.
 */
export const MOVES = [
  { id: 'armsup', icon: '🙌', verb: 'arms up' },
  { id: 'jump', icon: '⬆️', verb: 'jump' },
  { id: 'spin', icon: '🌀', verb: 'spin' },
  { id: 'wave', icon: '👋', verb: 'wave' },
  { id: 'clap', icon: '👏', verb: 'clap' },
] as const;

export type MoveId = (typeof MOVES)[number]['id'];

/** Large friendly animal icons used as a non-reading identity badge. */
export const ICONS = ['🦊', '🐢', '🐸', '🦜', '🐙', '🐝', '🦉', '🐬'] as const;

export const MAX_PLAYERS = COLOURS.length;

/** Pick identity assets for the Nth player (0-based). Wraps icons if needed. */
export function identityFor(index: number) {
  return {
    colour: COLOURS[index % COLOURS.length],
    tone: TONES[index % TONES.length],
    move: MOVES[index % MOVES.length],
    icon: ICONS[index % ICONS.length],
  };
}

export type { AssistLevel };
